/**
 * CountLabel — the product's ONE count grammar.
 *
 * Before this existed, six surfaces counted six different ways: "911 inquiries",
 * "175 of 911", "Showing 100 of 319", "66 open · 23 overdue", "852 inquiries
 * across 7 stages", "25 overdue of 25 loaded". Two of those are not counts at
 * all — they report how far the client happened to have paged, which is an
 * implementation detail the reader cannot act on and which changes as they
 * scroll.
 *
 * The rule this component encodes:
 *
 *   matching  what the server says matches the query on screen right now
 *   total     what the same query would match with the search and filters off
 *
 *   narrowed  →  "47 of 911 inquiries"
 *   whole     →  "911 inquiries"
 *   empty     →  "No inquiries match" / "No inquiries yet"
 *   unknown   →  a reserved-width blank, so the row never reflows on arrival
 *
 * `total` is only ever rendered when it is *larger* than `matching`; a caller who
 * passes the page buffer instead of the corpus gets `total === matching` and the
 * "of" clause silently disappears. There is deliberately no `loaded`, `shown`,
 * `page` or `visible` prop: a page-size artefact cannot be expressed through this
 * component's API at all.
 *
 * A second qualifier ("· 23 overdue") is not a count — it is a filter, and it
 * belongs in the Filter control, not here.
 *
 * `bare` drops the visible NOUN and nothing else — same forms, same arithmetic,
 * same sentence in the accessible name. It is for a head that repeats down a
 * row (the board's seven column heads), never for the toolbar.
 */

import type { HTMLAttributes } from 'react';

import { cn } from '@/lib/cn';
import { EN_DASH, compactNumber, pluralise } from '@/lib/format';
import { Skeleton } from './Skeleton';

export interface CountLabelProps extends Omit<HTMLAttributes<HTMLSpanElement>, 'children'> {
  /**
   * Rows matching the current query — the server's count, never `rows.length`.
   * `undefined`/`null` while the count is in flight.
   */
  matching: number | null | undefined;
  /**
   * Rows the same collection holds with search and filters cleared. Omit it when
   * nothing is narrowing the list. Never the client's page size.
   */
  total?: number | null;
  /** Singular noun: `inquiry`, `contact`, `company`, `task`. */
  noun: string;
  /** Irregular plural. Defaults to `noun + s`. */
  nounPlural?: string;
  /**
   * `md` in a toolbar, `sm` beside a heading or a column head.
   *
   * A count is a **number plus its unit**, and the two are not the same voice:
   * the number takes the row's control step (`md` → 13px/500, the step the
   * toolbar's own buttons run on) and the noun stays one rung down as the unit.
   * `md` used to set one 12px step for both, which put a type size on the
   * toolbar row that appeared in ten nodes on the whole page against 410 at
   * 13px and 277 at 11px — a rung used nowhere else, on the one row where the
   * chrome meets the content.
   */
  size?: 'sm' | 'md';
  /**
   * The count's *terminal* unknown. `matching: null` means "in flight" and
   * shimmers forever if the request that would resolve it has already failed —
   * measured on Analytics under a total endpoint failure: six red banners naming
   * what broke, and one 92×12 skeleton still animating beside them, the only
   * element on the page still claiming something was coming. `state="error"`
   * spends the same reserved width on an em dash, so the row still does not
   * reflow and the label stops lying.
   */
  state?: 'error';
  /**
   * How the *whole* collection is bounded, when the surface's entire dataset is
   * itself a slice — `in this period`. Analytics only, and it changes exactly
   * one string: the unfiltered-empty sentence, from `No inquiries yet` to
   * `No inquiries in this period`.
   *
   * The two empty forms are "a query is on" (`No <plural> match`) and "nothing
   * is" (`No <plural> yet`), and on a surface whose whole dataset is a reporting
   * *window* neither told the truth over an empty one. `match` asserted a filter
   * that did not exist — measured at `?range=custom&from=2019-01-01`, where
   * `conditions.length === 0` and the empty card 350px below correctly read "No
   * inquiries arrived between Jan 1 – Jan 5, 2019", so the two pieces of copy on
   * one screen disagreed about why the page was blank. And `yet` is a claim
   * about the corpus, which the shell contradicts three columns to the left with
   * "Inbox 41". This is the same sentence with its scope named, not a third
   * dialect: the verdict is still the count's, still stated once, and still the
   * headline the surface's empty state repeats.
   */
  emptyScope?: string;
  /**
   * Print the NUMBER without its unit, keeping the whole sentence in the
   * accessible name. Additive, defaults to `false`; the grammar, the forms and
   * the arithmetic are identical — only the visible noun is dropped.
   *
   * It exists for a repeated head, and the board is the case: "inquiries" was
   * printed six times in the top 130px of that screen — once in the toolbar and
   * once in each of five column heads — while each head is a 221px box already
   * holding a glyph, a stage name, an alarm badge and a ⋯ button, and the
   * section's own `aria-label` carries "New. 41 inquiries in New" regardless.
   * Linear and Height print a bare numeral in a board column head for exactly
   * this reason. The toolbar keeps the noun: it is the one place on the screen
   * the unit needs stating.
   *
   * Zero still prints — as `0`, not as a blank. A blank is not a number, and a
   * head that refuses to print one reads as an unloaded column rather than an
   * empty stage. The accessible name still resolves to `No inquiries match` /
   * `No inquiries yet`, which is the same verdict the toolbar states.
   */
  bare?: boolean;
}

/** Grouped, never compacted: a count is read, not skimmed. `1,204` — not `1.2k`. */
function grouped(value: number): string {
  return compactNumber(value, { compact: false });
}

export function CountLabel({
  matching,
  total,
  noun,
  nounPlural,
  size = 'md',
  state,
  emptyScope,
  bare = false,
  className,
  ...rest
}: CountLabelProps) {
  const pending = matching === null || matching === undefined || !Number.isFinite(matching);

  // Terminal, not pending: the same footprint, no shimmer, and a title that says
  // why the box is empty. Checked before `pending` because an errored query
  // reports `null` — indistinguishable from "still loading" without this.
  if (state === 'error' && pending) {
    return (
      <span
        aria-label="Count unavailable"
        title="Count unavailable"
        /* `--text-muted`, not `--text-faint`. The two are not a ramp: measured
           against `--surface-1` they are 5.35:1 and 5.80:1 in dark — "faint" is
           the STRONGER of the two, the opposite of what the names promise — and
           5.66:1 / 5.63:1 in light, a difference of half a percent nobody can
           see. A component that spends one of them beside the other on the same
           row is drawing a distinction that does not exist, so this label uses
           ONE neutral ink and buys its hierarchy from weight instead: the
           matching figure is `--text-secondary` at `font-medium`, everything
           around it is `--text-muted`. */
        className={cn(
          'tnum inline-flex w-[92px] shrink-0 items-center text-muted',
          size === 'sm' ? 'text-xs' : 'text-base',
          className,
        )}
        {...rest}
      >
        <span aria-hidden="true">{EN_DASH}</span>
      </span>
    );
  }

  // Reserves the row's width before the number lands — without this the whole
  // right-hand cluster (count, secondary action, primary action) slides left for
  // one frame on every query — and *shows* that it is holding it. An empty
  // reserved box was the one element on a list surface with no loading state:
  // every skeleton row, the toolbar and the grid held their geometry for the
  // whole fetch and then "183 companies" appeared out of blank space. Same
  // footprint, one shimmer, no pop-in.
  if (pending) {
    return (
      <span
        aria-hidden="true"
        className={cn('inline-flex w-[92px] shrink-0 items-center', className)}
        {...rest}
      >
        <Skeleton w="100%" h={size === 'sm' ? 10 : 13} rounded="xs" />
      </span>
    );
  }

  const count = matching as number;
  const corpus = total === null || total === undefined || !Number.isFinite(total) ? null : total;
  // `of` only survives when it says something true. Equal values mean "nothing is
  // narrowing this"; a smaller "total" means the caller handed us a page buffer.
  const narrowed = corpus !== null && corpus > count;
  const plural = pluralise(2, noun, nounPlural, { includeCount: false });
  // "1 of 911 inquiries" — in the narrowed form the noun agrees with the corpus,
  // not with the match, so a single result does not read "1 of 911 inquiry".
  const word = narrowed ? plural : pluralise(count, noun, nounPlural, { includeCount: false });

  // "Nothing is narrowing this" reads `No <plural> yet` — or, on a surface whose
  // whole collection is a slice, `No <plural> <scope>`. See `emptyScope`.
  const emptyText =
    corpus !== null ? `No ${plural} match` : `No ${plural} ${emptyScope ?? 'yet'}`;

  const sentence =
    count === 0
      ? emptyText
      : narrowed
        ? `${grouped(count)} of ${grouped(corpus)} ${word}`
        : `${grouped(count)} ${word}`;

  // The unit comes off the glyphs and stays in the sentence above. Zero is a
  // number, so it is printed like one.
  if (bare) {
    return (
      <span
        aria-live="polite"
        aria-label={sentence}
        className={cn('shrink-0 whitespace-nowrap text-muted tnum text-xs', className)}
        {...rest}
      >
        <span aria-hidden="true">
          <span className={cn('font-medium text-secondary', size === 'md' && 'text-base')}>
            {grouped(count)}
          </span>
          {/* Real spaces, not padding. Padding is what the reader sees and
              nothing else: selected, copied or read through `innerText` the
              padded form comes out `1of40`, which is not a number anyone can
              use. The unit is what `bare` drops; the word `of` is part of the
              figure. */}
          {narrowed ? <> of {grouped(corpus)}</> : null}
        </span>
      </span>
    );
  }

  return (
    <span
      aria-live="polite"
      aria-label={sentence}
      className={cn('shrink-0 whitespace-nowrap text-muted tnum text-xs', className)}
      {...rest}
    >
      {count === 0 ? (
        <span aria-hidden="true">{emptyText}</span>
      ) : (
        <span aria-hidden="true">
          <span className={cn('font-medium text-secondary', size === 'md' && 'text-base')}>
            {grouped(count)}
          </span>
          {narrowed ? (
            <>
              <span className="px-1">of</span>
              {grouped(corpus)}
            </>
          ) : null}{' '}
          {word}
        </span>
      )}
    </span>
  );
}
